import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { Button } from "../components/ui/button";
import {
  Loader2, RefreshCw, Inbox, AlertCircle, MessageSquare, Receipt, PhoneForwarded,
  CheckCircle2, X, ChevronRight, User, Package, Clock, Tag, Send,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  open: "Open", in_progress: "In progress", resolved: "Resolved", closed: "Closed",
};
const STATUS_STYLE = {
  open:        "bg-rose-100 text-rose-700 border-rose-200",
  in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  resolved:    "bg-emerald-100 text-emerald-700 border-emerald-200",
  closed:      "bg-slate-100 text-slate-600 border-slate-200",
};
const KIND_LABEL = { refund: "Refund", complaint: "Complaint", escalation: "Escalation" };
const KIND_ICON = { refund: Receipt, complaint: MessageSquare, escalation: PhoneForwarded };
const PRI_STYLE = {
  high:   "bg-rose-500 text-white",
  medium: "bg-amber-500 text-white",
  low:    "bg-slate-400 text-white",
};

export default function AdminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: "open", kind: null, priority: null });
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.kind) params.kind = filter.kind;
      if (filter.priority) params.priority = filter.priority;
      const { data } = await api.get("/admin/support/tickets", { params });
      setTickets(data.tickets || []);
      setSummary(data.summary);
    } catch {
      toast.error("Failed to load tickets");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.status, filter.kind, filter.priority]);

  const openDetail = async (t) => {
    try {
      const { data } = await api.get(`/admin/support/tickets/${t.id}`);
      setSelected(data);
    } catch { toast.error("Failed to open ticket"); }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="admin-support-tickets-page">
      <div className="flex items-center justify-between gap-3 animate-fadeInUp">
        <div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Inbox className="h-7 w-7 text-[#FF6B00]" /> Support Tickets Inbox
          </h1>
          <p className="text-sm text-slate-500 mt-1">Refunds, complaints, and escalations from AI support chat.</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="tickets-refresh-btn">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {/* Tile summary */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 animate-fadeInUp">
          <Tile label="Total" value={summary.total} />
          <Tile label="Open" value={summary.by_status?.open || 0} color="text-rose-700" />
          <Tile label="In progress" value={summary.by_status?.in_progress || 0} color="text-blue-700" />
          <Tile label="Resolved" value={summary.by_status?.resolved || 0} color="text-emerald-700" />
          <Tile label="High priority" value={summary.by_priority?.high || 0} color="text-rose-700" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 animate-fadeInUp">
        <FilterGroup label="Status">
          {[null, "open", "in_progress", "resolved", "closed"].map((s) => (
            <Chip key={s ?? "all-s"} active={filter.status === s}
                  onClick={() => setFilter((f) => ({ ...f, status: s }))}
                  data-testid={`filter-status-${s ?? "all"}`}>
              {s ? STATUS_LABEL[s] : "All"}
            </Chip>
          ))}
        </FilterGroup>
        <FilterGroup label="Type">
          {[null, "refund", "complaint", "escalation"].map((k) => (
            <Chip key={k ?? "all-k"} active={filter.kind === k}
                  onClick={() => setFilter((f) => ({ ...f, kind: k }))}
                  data-testid={`filter-kind-${k ?? "all"}`}>
              {k ? KIND_LABEL[k] : "All"}
            </Chip>
          ))}
        </FilterGroup>
        <FilterGroup label="Priority">
          {[null, "high", "medium", "low"].map((p) => (
            <Chip key={p ?? "all-p"} active={filter.priority === p}
                  onClick={() => setFilter((f) => ({ ...f, priority: p }))}
                  data-testid={`filter-pri-${p ?? "all"}`}>
              {p ? p.charAt(0).toUpperCase() + p.slice(1) : "All"}
            </Chip>
          ))}
        </FilterGroup>
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 animate-fadeInUp" data-testid="tickets-list">
        {loading ? (
          <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FF6B00]" /></div>
        ) : !tickets.length ? (
          <div className="p-16 text-center">
            <Inbox className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-base font-semibold text-slate-700">No tickets match these filters</p>
            <p className="text-sm text-slate-400 mt-1">Try widening the filter or click "All" above.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-50">
            {tickets.map((t) => {
              const KindIcon = KIND_ICON[t.kind] || MessageSquare;
              return (
                <li key={t.id} className="p-4 sm:p-5 flex items-center gap-3 hover:bg-slate-50 cursor-pointer transition-colors"
                    onClick={() => openDetail(t)}
                    data-testid={`ticket-row-${t.id}`}>
                  <div className="w-10 h-10 rounded-xl bg-slate-50 flex items-center justify-center shrink-0">
                    <KindIcon className="h-5 w-5 text-slate-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${STATUS_STYLE[t.status] || STATUS_STYLE.open}`}>
                        {STATUS_LABEL[t.status] || t.status}
                      </span>
                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${PRI_STYLE[t.priority] || PRI_STYLE.low}`}>
                        {t.priority || "low"}
                      </span>
                      <span className="text-xs font-semibold text-slate-700">{KIND_LABEL[t.kind] || t.kind}</span>
                      {t.tracking_id && (
                        <span className="text-[10px] font-mono text-slate-400">· {t.tracking_id}</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-900 mt-1.5 truncate">{t.summary}</p>
                    <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-2">
                      <User className="h-3 w-3" /> {t.user_name || t.user_email || "Unknown"}
                      <span>·</span>
                      <Clock className="h-3 w-3" /> {new Date(t.created_at).toLocaleString()}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-300 shrink-0" />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <TicketDetail ticket={selected} onClose={() => setSelected(null)} onChange={() => { load(); openDetail(selected); }} />
      )}
    </div>
  );
}

// ───────── Detail drawer ─────────
function TicketDetail({ ticket, onClose, onChange }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const changeStatus = async (status, withNote = false) => {
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${ticket.id}/status`,
                     withNote && note ? { status, note } : { status });
      toast.success(`Marked ${STATUS_LABEL[status] || status}`);
      if (withNote) setNote("");
      onChange();
    } catch (e) { toast.error("Update failed"); }
    finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/support/tickets/${ticket.id}/notes`, { text: note });
      toast.success("Note added");
      setNote("");
      onChange();
    } catch { toast.error("Failed to add note"); }
    finally { setBusy(false); }
  };

  const KindIcon = KIND_ICON[ticket.kind] || MessageSquare;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="ticket-detail-drawer">
      <button className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-label="Close" />
      <div className="relative bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl animate-slideInRight">
        <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <KindIcon className="h-5 w-5 text-[#FF6B00]" />
            <h2 className="font-['Outfit'] text-lg font-bold">{KIND_LABEL[ticket.kind] || ticket.kind} · {ticket.id.slice(-6)}</h2>
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border ${STATUS_STYLE[ticket.status]}`}>{STATUS_LABEL[ticket.status]}</span>
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${PRI_STYLE[ticket.priority] || PRI_STYLE.low}`}>{ticket.priority || "low"}</span>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg" data-testid="ticket-detail-close"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Customer */}
          <Section title="Customer">
            <div className="flex items-center gap-3 text-sm">
              <User className="h-4 w-4 text-slate-400" />
              <span className="font-semibold text-slate-900">{ticket.user?.name || "Unknown"}</span>
              <span className="text-slate-500">{ticket.user?.email}</span>
              <span className="text-slate-500">· {ticket.user?.phone || "—"}</span>
            </div>
          </Section>

          {/* Order */}
          {ticket.order && (
            <Section title="Linked order" testid="ticket-linked-order">
              <div className="text-sm space-y-1">
                <div className="flex items-center gap-2"><Package className="h-3.5 w-3.5 text-slate-400" /><span className="font-mono">{ticket.tracking_id}</span><span className="px-2 py-0.5 rounded bg-slate-100 text-[10px] font-bold uppercase">{ticket.order.status}</span></div>
                <p className="text-slate-600">{ticket.order.pickup_address} → {ticket.order.delivery_address}</p>
                <p className="text-slate-500 text-xs">Agent: {ticket.order.agent_name || "—"} · ₹{((ticket.order.price?.final_price) || 0).toLocaleString()} · {new Date(ticket.order.created_at).toLocaleString()}</p>
              </div>
            </Section>
          )}

          {/* Summary */}
          <Section title="Customer message">
            <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-3 leading-relaxed">{ticket.summary}</p>
          </Section>

          {/* Recent chat */}
          {!!ticket.chat_recent?.length && (
            <Section title="Recent chat context" testid="ticket-chat-context">
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {ticket.chat_recent.map((m, i) => (
                  <div key={i} className={`text-xs px-3 py-1.5 rounded-lg ${m.role === "user" ? "bg-slate-900 text-white ml-6" : "bg-slate-50 text-slate-700 mr-6"}`}>
                    <span className="opacity-60 mr-2">{m.role}</span>{m.text}
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Internal notes */}
          <Section title="Internal notes">
            {!ticket.notes?.length ? (
              <p className="text-xs text-slate-400 italic">No notes yet.</p>
            ) : (
              <ul className="space-y-2">
                {ticket.notes.map((n, i) => (
                  <li key={i} className="text-xs bg-amber-50 border border-amber-100 rounded-lg p-2">
                    <p className="text-slate-700">{n.text}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{n.by} · {new Date(n.ts).toLocaleString()}{n.kind === "status_change" ? ` · → ${STATUS_LABEL[n.to_status]}` : ""}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex gap-2">
              <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Add internal note (not visible to customer)…"
                     className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm"
                     data-testid="ticket-note-input" />
              <Button onClick={addNote} disabled={busy || !note.trim()} className="bg-slate-900 hover:bg-slate-800 text-white gap-1" data-testid="ticket-note-add">
                <Send className="h-3.5 w-3.5" /> Add
              </Button>
            </div>
          </Section>

          {/* Action buttons */}
          <Section title="Update status">
            <div className="flex flex-wrap gap-2">
              {ticket.status === "open" && (
                <Button onClick={() => changeStatus("in_progress")} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white gap-1" data-testid="ticket-status-in-progress">
                  <Clock className="h-3.5 w-3.5" /> Start working
                </Button>
              )}
              {ticket.status !== "resolved" && ticket.status !== "closed" && (
                <Button onClick={() => changeStatus("resolved", true)} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1" data-testid="ticket-status-resolved">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark resolved
                </Button>
              )}
              {ticket.status === "resolved" && (
                <Button onClick={() => changeStatus("open")} disabled={busy} variant="outline" data-testid="ticket-status-reopen">
                  Re-open
                </Button>
              )}
              {ticket.status !== "closed" && (
                <Button onClick={() => changeStatus("closed", true)} disabled={busy} variant="outline" data-testid="ticket-status-close">
                  Close ticket
                </Button>
              )}
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">Tip: type a note above + click "Mark resolved" to attach the resolution rationale.</p>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ───────── Tiny helpers ─────────
function Section({ title, children, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">{title}</p>
      {children}
    </div>
  );
}
function Tile({ label, value, color = "text-slate-700" }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{label}</p>
      <p className={`text-2xl font-bold ${color} font-['Outfit']`}>{value}</p>
    </div>
  );
}
function FilterGroup({ label, children }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold pr-1">{label}:</span>
      {children}
    </div>
  );
}
function Chip({ active, children, ...rest }) {
  return (
    <button
      type="button"
      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}
      {...rest}
    >{children}</button>
  );
}
